/**
 * Typed API error. The auditor server speaks one FLAT error envelope
 * (E2-API-REFERENCE.md "Error bodies"): `{ error: "<code>", message: "<string>" }`
 * -- unlike shop/server's nested `{ error: { code, message } }`. The fetch
 * core turns any non-2xx response into an ApiError so callers branch on a
 * stable machine `code` (e.g. 'forbidden', 'shop_not_found') rather than
 * parsing bodies or status codes by hand.
 */
export class ApiError extends Error {
  /** Short machine code from the envelope (or a synthetic one below). */
  readonly code: string;
  /** HTTP status, or 0 for a transport failure that never reached the server. */
  readonly status: number;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }

  /** True when the request never got an HTTP response (offline, server down). */
  get isNetworkError(): boolean {
    return this.status === 0;
  }

  /** True for the login rate-limit response (429 rate_limited). */
  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

/** Shape of the server error envelope; kept tolerant of a legacy/nested form
 *  in case a future route ever mirrors shop-server's shape instead. */
interface ErrorEnvelope {
  error?: { code?: unknown; message?: unknown } | string;
  message?: unknown;
  code?: unknown;
}

/**
 * Coerce a parsed error body into an ApiError. Canonical auditor form is the
 * FLAT `{ error: "<code>", message: "<string>" }`; a nested `{ error: {
 * code, message } }` is also tolerated so a mismatch between server versions
 * degrades to a usable error, not a crash.
 */
export function apiErrorFromBody(status: number, body: unknown): ApiError {
  const env = (body ?? {}) as ErrorEnvelope;
  let code: string | undefined;
  let message: string | undefined;

  if (typeof env.error === 'string') {
    code = env.error;
  } else if (env.error && typeof env.error === 'object') {
    const inner = env.error;
    if (typeof inner.code === 'string') code = inner.code;
    if (typeof inner.message === 'string') message = inner.message;
  }
  if (!code && typeof env.code === 'string') code = env.code;
  if (!message && typeof env.message === 'string') message = env.message;

  return new ApiError(status, code ?? `http_${status}`, message ?? `request failed (${status})`);
}
